import React, { useEffect } from 'react';
import { ArrowRight, Star, TrendingUp, Users } from 'lucide-react';
import SEO from './SEO';

interface Props {
  onNavigate: (view: any, sectionId?: string) => void;
}

const SupplementsForInfluencers: React.FC<Props> = ({ onNavigate }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 pb-16 bg-white">
      <SEO 
        title="Start a Supplement Brand for Influencers | BuildMySupplement" 
        description="You have the audience. Let us handle the manufacturing. Build your own supplement brand as an influencer with our premium white label solutions." 
      />
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-100/80 border border-orange-200 mb-6">
            <span className="text-orange-800 text-xs font-bold uppercase tracking-widest">
              Monetize Your Influence
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 tracking-tight mb-8 leading-tight">
            Turn Your Audience Into an <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">Enduring Legacy</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto">
            Stop giving your engagement away for 10% affiliate commissions. You already have the hardest asset in business to acquire: loyal attention. We provide the world-class manufacturing, making it easier than ever to build a premium <strong>supplement brand for influencers</strong>.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-black rounded-full hover:bg-gray-800 transition-all duration-200 transform hover:-translate-y-1 shadow-lg"
          >
            Launch Your Creator Brand <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Content Sections */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-lg text-gray-700 space-y-12">
        
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">The Evolution of the Creator Economy</h2>
          <p className="mb-4 leading-relaxed">
            The era of merch drops and simple brand sponsorships is evolving. Today's most successful health creators, fitness influencers, and wellness gurus are no longer just faces for corporate campaigns—they are CEOs of their own DTC empires. When you learn <strong>how to start a supplement brand as an influencer</strong>, you transition from renting your audience out to owning a compounding business asset. 
          </p>
          <p className="mb-4 leading-relaxed">
             Why sell someone else's pre-workout for a tiny fractional payout when your community is buying it specifically because <em>you</em> recommended it? By bottling your own ethos and building an <strong>influencer supplement line</strong>, you capture the full margin while delivering a product you can genuinely stand behind.
          </p>
        </div>

        <div>
           <h2 className="text-3xl font-extrabold text-gray-900 mb-6">White Label Supplements for Creators: The Ultimate Shortcut</h2>
           <p className="mb-4 leading-relaxed">
            Many creators hesitate to launch physical goods because the logistics sound like a nightmare. You manage content calendars, brand deals, and video editing—you don't have time to interview chemical engineers or source raw ashwagandha from multiple continents.
           </p>
           <p className="mb-4 leading-relaxed">
             This is precisely why we engineered our manufacturing floors for creators. Using <strong>white label supplements creators</strong> can bypass years of frustrating research and development. We maintain a vault of over 100 scientifically proven, heavily vetted formulations that are simply waiting for your beautiful label. Whether your audience wants a clinically dosed pump product, a clean vegan protein, or a nootropic focus blend, we have it in stock and ready to ship. You bring the brand and the marketing firepower; we completely handle the supply chain.
           </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Zero Barriers: Why You Don't Need Huge Capital</h2>
          <p className="mb-4 leading-relaxed">
            The traditional manufacturing model hates the influencer. Legacy factories demand 10,000 unit minimums, tying up tens of thousands of dollars of your personal capital before you even know if your product will convert. We tore that model down.
          </p>
          <p className="mb-4 leading-relaxed">
            We operate with zero minimum order quantities on our white label catalog. If you want to do a "limited drop" of 100 units of a fat burner to test your audience's appetite, you can. If that drop sells out in thirty seconds, our scalable infrastructure can step in and manufacture 5,000 units for your next massive restock. We remove the initial financial risk, allowing you to iterate quickly and build a hyper-profitable business without a venture capital loan.
          </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Aesthetic First: We Understand Creator Branding</h2>
          <p className="mb-4 leading-relaxed">
            If a supplement looks like it was bought at a discount pharmacy in 2004, your audience will not buy it. We know that as a creator, your aesthetic is your brand. It must look premium on an Instagram Reel. It must pop in a YouTube thumbnail.
          </p>
          <p className="mb-4 leading-relaxed">
            Our in-house design team collaborates directly with you to ensure your <strong>influencer supplement line</strong> looks like a world-class luxury wellness brand. We ensure your labels are not only visually breathtaking but also legally compliant with local and international guidelines. Your product will look so good that your community won't just consume it—they will post it to their own stories.
          </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Your Audience is Waiting. Here is the Blueprint.</h2>
          <p className="mb-4 leading-relaxed">
            The steps to launching your creator brand are radically streamlined when you partner with the right manufacturer. 
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="font-bold text-gray-900">1. Assess Your Niche</h4>
              <p className="text-sm text-gray-600 mt-2">Listen to your DMs. What is your community struggling with? Sleep? Energy? Weight Management?</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
                <Star className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="font-bold text-gray-900">2. Select the Product</h4>
              <p className="text-sm text-gray-600 mt-2">Pick an impeccably formulated, ready-to-sell product from our catalog that answers their exact problem.</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
                <TrendingUp className="w-8 h-8 text-orange-600" />
              </div>
              <h4 className="font-bold text-gray-900">3. Drop & Scale</h4>
              <p className="text-sm text-gray-600 mt-2">Hype the drop, launch your styled products, and let our rapid turnaround keep you continually in stock.</p>
            </div>
          </div>
        </div>

      </section>

      {/* CTA Section */}
      <section className="bg-black text-white py-24 mt-12 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight">Stop Building Other People's Brands.</h2>
          <p className="text-xl text-gray-400 mb-10">
            Partner with the manufacturer trusted by top creators. We handle the science and the supply chain. You handle the take-over.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-black bg-orange-500 rounded-full hover:bg-orange-400 transition-all duration-200 transform hover:-translate-y-1"
          >
            Launch Your Brand <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

    </div>
  );
};

export default SupplementsForInfluencers;
